import envConfig from "../../../envConfig";
import Common from "../Common";

// Region-aware base URL for the Fine-Tuning API.
// Servers (per the OpenAPI spec):
//   aus -> https://api.toothfairyai.com/finetuning
//   eu  -> https://api.eu.toothfairyai.com/finetuning
//   us  -> https://api.us.toothfairyai.com/finetuning
//   dev -> https://api.toothfairylab.link/finetuning
export const getFinetuningBaseUrl = () => {
  if (envConfig?.REACT_APP_ENV === "dev") {
    return "https://api.toothfairylab.link/finetuning";
  }
  const region = envConfig?.REACT_APP_REGION;
  if (region === "eu" || region === "us") {
    return `https://api.${region}.toothfairyai.com/finetuning`;
  }
  return "https://api.toothfairyai.com/finetuning";
};

const buildUrl = (path, params) => {
  let url = `${getFinetuningBaseUrl()}${path}`;
  if (params && Object.keys(params).length > 0) {
    const cleaned = Common.objectCleaner(params);
    // objectCleaner keeps undefined values, and URLSearchParams serializes
    // them as the literal "undefined" (e.g. ?base_model=undefined) — drop
    // them so optional params are simply omitted from the query string.
    Object.keys(cleaned).forEach((key) => {
      if (cleaned[key] === undefined) delete cleaned[key];
    });
    const search = new URLSearchParams(cleaned).toString();
    if (search) url += `?${search}`;
  }
  return url;
};

const finetuningFetch = async (
  path,
  { method = "GET", token, body, query } = {}
) => {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-api-key"] = token;
  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body ? JSON.stringify(Common.objectCleaner(body)) : undefined,
  });
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = text;
  }
  if (!response.ok) {
    const message =
      json?.error?.message ||
      json?.message ||
      json?.detail ||
      `Fine-tuning API error (${response.status})`;
    const err = new Error(message);
    err.status = response.status;
    err.body = json;
    throw err;
  }
  return json;
};

// --- Models (public) ---------------------------------------------------------
const listTrainableModels = () => finetuningFetch("/models");

// --- Jobs --------------------------------------------------------------------
const listTrainingJobs = ({ token, status, limit, nextToken } = {}) =>
  finetuningFetch("/jobs", {
    token,
    query: { status, limit, nextToken },
  });

// --- Dataset -----------------------------------------------------------------
const generateDataset = ({ token, ...payload }) =>
  finetuningFetch("/dataset", { method: "POST", token, body: payload });

const downloadDataset = ({ token, trainingLogId }) =>
  finetuningFetch(`/dataset/${trainingLogId}`, { token });

// --- Training ----------------------------------------------------------------
const getTrainingStatus = ({ token, trainingLogId }) =>
  finetuningFetch(`/status/${trainingLogId}`, { token });

const startTraining = ({ token, trainingLogId, config }) =>
  finetuningFetch(`/start/${trainingLogId}`, {
    method: "POST",
    token,
    body: { config },
  });

const cancelTraining = ({ token, trainingLogId }) =>
  finetuningFetch(`/cancel/${trainingLogId}`, { method: "POST", token });

// --- Adapters ----------------------------------------------------------------
const listAdapters = ({ token, limit, nextToken } = {}) =>
  finetuningFetch("/adapters", { token, query: { limit, nextToken } });

const snapshotAdapterVersion = ({ token, trainingLogId, label }) =>
  finetuningFetch(`/adapters/${trainingLogId}/version`, {
    method: "POST",
    token,
    body: { label },
  });

const rollbackAdapter = ({ token, trainingLogId, version }) =>
  finetuningFetch(`/adapters/${trainingLogId}/rollback`, {
    method: "POST",
    token,
    body: { version },
  });

// --- Serving -----------------------------------------------------------------
const getServing = ({ token, trainingLogId }) =>
  finetuningFetch(`/serving/${trainingLogId}`, { token });

const setServing = ({ token, trainingLogId, mode, schedule }) =>
  finetuningFetch(`/serving/${trainingLogId}`, {
    method: "POST",
    token,
    body: { mode, schedule },
  });

const setServingPower = ({ token, trainingLogId, state, hours }) =>
  finetuningFetch(`/serving/${trainingLogId}/power`, {
    method: "POST",
    token,
    body: { state, hours },
  });

// --- Pricing -----------------------------------------------------------------
// Generic endpoint: no params -> full tier table; baseModel or trainingLogId
// -> pricing resolved for that model.
const getPricing = ({ token, baseModel, trainingLogId } = {}) =>
  finetuningFetch("/pricing", {
    token,
    query: { base_model: baseModel, trainingLogId },
  });

export default {
  getFinetuningBaseUrl,
  listTrainableModels,
  listTrainingJobs,
  generateDataset,
  downloadDataset,
  getTrainingStatus,
  startTraining,
  cancelTraining,
  listAdapters,
  snapshotAdapterVersion,
  rollbackAdapter,
  getServing,
  setServing,
  setServingPower,
  getPricing,
};
